import { InfoboxService } from '@/common/infobox/infobox.service';
import { NgStyle } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	Injector,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { finalize, tap } from 'rxjs';
import { ImageUploaderApiService } from './image-uploader.api.service';

@Component({
	selector: 'app-image-uploader',
	standalone: true,
	imports: [MatButton, MatProgressSpinner, MatTooltip, NgStyle],
	templateUrl: './image-uploader.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styles: [
		`
			.custom-alert {
				font-size: 13px;
			}
		`,
	],
})
export class ImageUploaderComponent {
	currentImage = input<string>(null);
	maxImageSize = input(5 * 1024 * 1024); //Default 5MB
	uploaderPxHeight = input(250);
	uploaderPxWidth = input(250);

	imageUploaded = output<{ id: string; url: string }>();

	readonly selectedImage = signal('');

	readonly imageLoadingState = signal(false);

	readonly uploaderDimensions = computed(() => {
		return {
			width: `${this.uploaderPxWidth()}px`,
			height: `${this.uploaderPxHeight()}px`,
		};
	});

	readonly showSaveImageWarning = computed(() => {
		return this.currentImage() !== this.selectedImage();
	});

	private readonly snackbar = inject(MatSnackBar);
	private readonly infobox = inject(InfoboxService);
	private readonly imageUploaderApiService = inject(ImageUploaderApiService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly injector = inject(Injector);

	ngOnInit(): void {
		toObservable(this.currentImage, { injector: this.injector })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.selectedImage.set(value);
			});
	}

	processImage(event: Event): void {
		this.imageLoadingState.set(true);
		const input = event.target as HTMLInputElement;

		if (!input || !input.files || input.files.length === 0) {
			this.imageLoadingState.set(false);
			return;
		}

		const file = input.files[0];

		if (file.size >= this.maxImageSize()) {
			this.imageLoadingState.set(false);
			this.infobox.error('Wybrane zdjęcie jest zbyt duże');
			return;
		}

		let formData = new FormData();
		formData.append('name', file.name);
		formData.append('file', file);

		this.imageUploaderApiService
			.uploadImage(formData)
			.pipe(
				tap(() => {
					formData = null;
				}),
				finalize(() => {
					this.imageLoadingState.set(false);
				}),
			)
			.subscribe((response) => {
				this.selectedImage.set(response.url);

				this.imageUploaded.emit(response);

				this.snackbar.open('Zdjęcie zostało dodane pomyślnie', 'Zamknij', { duration: 2000 });
			});
	}
}
